import React, { useCallback, useEffect, useRef, useState } from 'react';
import ModalService from '../services/Modal.service';
import '../payment.css';

const STRIPE_JS = 'https://js.stripe.com/v3/';
const RECAPTCHA_JS = 'https://www.google.com/recaptcha/api.js?render=explicit';

// Same list, order and display names as the Request-a-Quote modal (Pricing.js),
// so a quote and a card payment describe the tiers identically.
const SUBSCRIPTION_TYPES = [
  { value: 'jr', display: 'Tuva Jr.' },
  { value: 'simulation', display: 'Tuva Sims' },
  { value: 'premium', display: 'Tuva Premium' },
  { value: 'ose_companion', display: 'OpenSciEd Companion' },
  { value: 'assessments', display: 'Assessments' },
];

// Styling for the fields Stripe renders inside its own iframes — it can only be
// set through this object, page CSS cannot reach into them.
// Matches the text inputs: 15px/#1f2937 on a 45.5px box with 10px padding.
const ELEMENT_STYLE = {
  base: {
    iconColor: '#666EE8',
    color: '#1f2937',
    lineHeight: '24px',
    fontWeight: 400,
    fontFamily: 'Source Sans Pro, sans-serif, Arial',
    fontSize: '15px',
    '::placeholder': {
      color: '#d6dae0',
    },
  },
};

const loadScript = src =>
  new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${src}"]`);
    if (script) {
      if (script.dataset.loaded === 'true') {
        resolve();
        return;
      }
    } else {
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', reject);
  });

// Keeps the amount input numeric while typing, matching the old displayAmount().
const sanitizeAmount = value => {
  if (!isNaN(value)) {
    return value;
  }
  let sanitized = value.replace(/[^0-9.]/g, '');
  if (sanitized.split('.').length > 2) {
    sanitized = sanitized.replace(/\.+$/, '');
  }
  return sanitized;
};

const labelClass = hasError => (hasError ? 'is-invalid' : undefined);
const fieldClass = (base, hasError) => `${base}${hasError ? ' is-invalid' : ''}`;

// Required-field label with a red asterisk, same as the Pricing modal.
const req = label => (
  <>
    {label} <span className="req-star">*</span>
  </>
);

function Payment({ stripeKey, captchaKey, email: defaultEmail, fullName: defaultFullName }) {
  const [fullName, setFullName] = useState(defaultFullName || '');
  const [email, setEmail] = useState(defaultEmail || '');
  const [amount, setAmount] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [errorText, setErrorText] = useState('');
  // No captcha key configured (e.g. local dev) — don't lock the button behind a
  // widget that will never render.
  const [captchaVerified, setCaptchaVerified] = useState(!captchaKey);
  const [captchaError, setCaptchaError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaid, setIsPaid] = useState(false);

  const cardNumberRef = useRef();
  const cardExpiryRef = useRef();
  const cardCvcRef = useRef();
  const captchaRef = useRef();
  const stripeRef = useRef();
  const cardNumberElementRef = useRef();

  // Mount the three Stripe Elements (card number / expiry / cvc).
  useEffect(() => {
    if (!stripeKey) {
      setErrorText('Payments are unavailable right now. Please try again later.');
      return undefined;
    }

    let elements = [];
    let cancelled = false;

    loadScript(STRIPE_JS)
      .then(() => {
        if (cancelled) {
          return;
        }
        if (!window.Stripe) {
          // Script responded but the global never appeared — usually a blocker
          // extension or proxy serving an empty body. Say so instead of leaving
          // three empty boxes on screen.
          throw new Error('Stripe.js did not load');
        }
        const stripe = window.Stripe(stripeKey);
        const stripeElements = stripe.elements();

        const cardNumber = stripeElements.create('cardNumber', {
          style: ELEMENT_STYLE,
          placeholder: 'xxxx xxxx xxxx xxxx',
        });
        const cardExpiry = stripeElements.create('cardExpiry', {
          style: ELEMENT_STYLE,
          placeholder: 'MM/YY',
        });
        const cardCvc = stripeElements.create('cardCvc', {
          style: ELEMENT_STYLE,
          placeholder: 'CVV/CVV2',
        });

        cardNumber.mount(cardNumberRef.current);
        cardExpiry.mount(cardExpiryRef.current);
        cardCvc.mount(cardCvcRef.current);

        elements = [cardNumber, cardExpiry, cardCvc];
        stripeRef.current = stripe;
        cardNumberElementRef.current = cardNumber;
      })
      .catch(() => {
        setErrorText('Payments are unavailable right now. Please try again later.');
      });

    return () => {
      cancelled = true;
      elements.forEach(element => element.destroy());
    };
  }, [stripeKey]);

  // Render the reCAPTCHA widget that gates the Pay button.
  useEffect(() => {
    if (!captchaKey) {
      return undefined;
    }

    let cancelled = false;
    const render = () => {
      // Already rendered (StrictMode remounts the effect in dev).
      if (cancelled || !captchaRef.current || captchaRef.current.childNodes.length) {
        return;
      }
      window.grecaptcha.render(captchaRef.current, {
        sitekey: captchaKey,
        callback: () => {
          setCaptchaError('');
          setCaptchaVerified(true);
        },
        'expired-callback': () => setCaptchaVerified(false),
        // Fires for an invalid/unregistered site key. Without this the widget
        // just says "ERROR for site owner" and Pay Now stays grey forever with
        // no explanation on our side.
        'error-callback': () =>
          setCaptchaError(
            "Couldn't verify the reCAPTCHA. Please reload the page — if it keeps failing, the site key isn't valid for this domain.",
          ),
      });
    };

    loadScript(RECAPTCHA_JS)
      .then(() => {
        if (cancelled) {
          return;
        }
        if (!window.grecaptcha) {
          throw new Error('reCAPTCHA did not load');
        }
        // `ready` exists on the v3 API and recent v2 builds only.
        if (typeof window.grecaptcha.ready === 'function') {
          window.grecaptcha.ready(render);
        } else {
          render();
        }
      })
      .catch(() =>
        setCaptchaError(
          "Couldn't load the reCAPTCHA. Please check your connection and reload the page.",
        ),
      );

    return () => {
      cancelled = true;
    };
  }, [captchaKey]);

  const validate = useCallback(() => {
    const nextErrors = {};
    let message = '';

    if (!fullName.trim()) {
      nextErrors.fullName = true;
      message += 'Your name is incomplete. \n';
    }
    if (!subscriptions.length) {
      nextErrors.subscription = true;
      message += 'Please select at least one subscription type. \n';
    }
    if (!amount) {
      nextErrors.amount = true;
      message += 'Your amount is incomplete. \n';
    } else if (parseFloat(amount) <= 0) {
      nextErrors.amount = true;
      message += "The amount you've entered must be more than $0.00. \n";
    }
    if (!email.trim()) {
      nextErrors.email = true;
      message += 'Your email is incomplete.';
    }

    setErrors(nextErrors);
    setErrorText(message);
    return !message;
  }, [fullName, subscriptions, amount, email]);

  const toggleSubscription = useCallback(display => {
    setSubscriptions(prev =>
      prev.includes(display)
        ? prev.filter(item => item !== display)
        : // Keep the checkbox order rather than click order.
          SUBSCRIPTION_TYPES.filter(
            type => type.display === display || prev.includes(type.display),
          ).map(type => type.display),
    );
  }, []);

  // Stripe reports card problems as a single sentence — flag the field it names,
  // the way the old jQuery handler did.
  const applyCardError = useCallback(message => {
    setErrors({
      cardNumber: message.includes('card number'),
      cardExpiry: message.includes('expiration date'),
      cardCvc: message.includes('security code'),
    });
    setErrorText(message);
  }, []);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();
      setErrors({});
      setErrorText('');

      if (!validate() || !stripeRef.current || !cardNumberElementRef.current) {
        return;
      }

      setIsProcessing(true);
      const result = await stripeRef.current.createToken(
        cardNumberElementRef.current,
      );

      if (result.error) {
        applyCardError(result.error.message);
        setIsProcessing(false);
        return;
      }

      const response = await ModalService.charge({
        token: result.token.id,
        amount,
        email,
        fullName,
        subscription: subscriptions.join(', '),
      });

      if (response.payment_status === 'success') {
        setIsPaid(true);
        return;
      }

      setErrorText(response.message || 'Failed! Please try again later!');
      setIsProcessing(false);
      // The token is spent; the captcha has to be solved again before retrying.
      if (captchaKey && window.grecaptcha) {
        window.grecaptcha.reset();
        setCaptchaVerified(false);
      }
    },
    [
      validate,
      applyCardError,
      amount,
      email,
      fullName,
      subscriptions,
      captchaKey,
    ],
  );

  if (isPaid) {
    return (
      <div className="payment-page">
        <div className="payment-card">
          <div className="payment-card-header">
            <h5>Payment Successful</h5>
          </div>
          <div className="payment-card-body payment-success">
            <p>Thank you for submitting the payment.</p>
            <p>
              The transaction was successful. We have emailed you the receipt
              for the purchase.
            </p>
            <p>
              If you have any questions, you can call us at{' '}
              <a href="tel:+1.646.481.2150">+1.646.481.2150</a> or email{' '}
              <a href="mailto:support@tuvalabs.com">Tuva Support</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <div className="payment-card">
        <div className="payment-card-header">
          <h5>
            Please Enter Your Payment Information for Your Tuva Premium
            Subscription and Add-Ons
          </h5>
        </div>
        <div className="payment-card-body">
          <form onSubmit={onSubmit}>
            {errorText && (
              <div className="payment-error" role="alert">
                {errorText}
              </div>
            )}
            <div className="payment-field">
              <label
                className={labelClass(errors.fullName)}
                htmlFor="cardholdername"
              >
                {req('Full Name')}
              </label>
              <input
                type="text"
                id="cardholdername"
                name="cardholdername"
                maxLength="70"
                placeholder="Card Holder Name"
                className={fieldClass('payment-input', errors.fullName)}
                value={fullName}
                onChange={e => setFullName(e.target.value)}
              />
            </div>
            <div className="payment-field">
              <label className={labelClass(errors.subscription)}>
                {req('Subscription Type')}
              </label>
              <div className="payment-checkbox-group">
                {SUBSCRIPTION_TYPES.map(type => (
                  <label className="payment-checkbox" key={type.value}>
                    <input
                      type="checkbox"
                      name="subscription"
                      value={type.display}
                      checked={subscriptions.includes(type.display)}
                      onChange={() => toggleSubscription(type.display)}
                    />
                    {type.display}
                  </label>
                ))}
              </div>
            </div>
            <div className="payment-field">
              <label className={labelClass(errors.cardNumber)}>
                {req('Card Number')}
              </label>
              <div
                ref={cardNumberRef}
                className={fieldClass('field', errors.cardNumber)}
              />
            </div>
            <div className="payment-row">
              <div className="payment-field">
                <label className={labelClass(errors.cardExpiry)}>
                  {req('Card Expiry Date')}
                </label>
                <div
                  ref={cardExpiryRef}
                  className={fieldClass('field', errors.cardExpiry)}
                />
              </div>
              <div className="payment-field">
                <label className={labelClass(errors.cardCvc)}>
                  {req('CVV/CVV2')}
                </label>
                <div
                  ref={cardCvcRef}
                  className={fieldClass('field', errors.cardCvc)}
                />
              </div>
            </div>
            <div className="payment-field">
              <label className={labelClass(errors.amount)} htmlFor="amount">
                {req('Amount (USD)')}
              </label>
              <input
                type="text"
                id="amount"
                name="amount"
                placeholder="Amount"
                className={fieldClass('payment-input', errors.amount)}
                value={amount}
                onChange={e => setAmount(sanitizeAmount(e.target.value))}
                onBlur={() => {
                  const parsed = parseFloat(amount);
                  setAmount(isNaN(parsed) ? '' : parsed.toFixed(2));
                }}
              />
            </div>
            <div className="payment-field">
              <label className={labelClass(errors.email)} htmlFor="email">
                {req('Email')}
              </label>
              <input
                type="text"
                id="email"
                name="email"
                maxLength="65"
                placeholder="Email"
                className={fieldClass('payment-input', errors.email)}
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="payment-captcha">
              <div ref={captchaRef} className="g-recaptcha" />
            </div>
            {captchaError && (
              <div className="payment-error" role="alert">
                {captchaError}
              </div>
            )}
            <div className="payment-actions">
              <button
                className="payment-submit"
                type="submit"
                disabled={!captchaVerified || isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Pay Now'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Payment;
